(() => {
  'use strict';
  if (customElements.get('bt-cart-policy')) return;

  const panels = new Set();
  let policyModal;
  let policyLoaded = false;
  let policyLoading = false;
  let policyOpener;
  const isArabic = () => document.documentElement.lang.toLowerCase().startsWith('ar');
  function loadPolicyAsset(url, style) {
    const absolute = new URL(url, location.href).href;
    const existing = [...document.querySelectorAll(style ? 'link[rel="stylesheet"]' : 'script[src]')]
      .find((node) => (style ? node.href : node.src) === absolute);
    if (existing) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const node = document.createElement(style ? 'link' : 'script');
      if (style) { node.rel = 'stylesheet'; node.href = absolute; }
      else node.src = absolute;
      node.onload = resolve;
      node.onerror = () => { node.remove(); reject(new Error('Policy asset unavailable')); };
      document.head.append(node);
    });
  }
  async function openPolicy(link) {
    policyOpener = link;
    if (!policyModal) {
      policyModal = document.createElement('dialog');
      policyModal.className = 'bt-cart-policy-modal';
      policyModal.dir = isArabic() ? 'rtl' : 'ltr';
      policyModal.setAttribute('aria-labelledby', 'bt-cart-policy-modal-title');
      policyModal.innerHTML = `<header class="bt-cart-policy-modal__header"><h2 id="bt-cart-policy-modal-title"></h2><button type="button" class="bt-cart-policy-modal__close" autofocus><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg></button></header><div class="bt-cart-policy-modal__body"></div>`;
      policyModal.querySelector('h2').textContent = isArabic() ? 'الشحن والاسترجاع' : 'Shipping & returns';
      const close = policyModal.querySelector('button');
      close.setAttribute('aria-label', isArabic() ? 'إغلاق' : 'Close');
      close.addEventListener('click', () => policyModal.close());
      let backdropDown = false;
      const outside = (event) => {
        const rect = policyModal.getBoundingClientRect();
        return event.target === policyModal && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
      };
      policyModal.addEventListener('pointerdown', (event) => { backdropDown = outside(event); });
      policyModal.addEventListener('click', (event) => { if (backdropDown && outside(event)) policyModal.close(); });
      // Keep Escape inside this top-layer dialog, leaving the cart drawer open.
      policyModal.addEventListener('keydown', (event) => { if (event.key === 'Escape') event.stopPropagation(); });
      policyModal.addEventListener('close', () => { sync(); if (policyOpener?.isConnected) policyOpener.focus(); });
      document.body.append(policyModal);
    }
    if (!policyModal.open) policyModal.showModal();
    if (policyLoaded || policyLoading) return;
    policyLoading = true;
    const body = policyModal.querySelector('.bt-cart-policy-modal__body');
    body.setAttribute('aria-busy', 'true');
    body.replaceChildren();
    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.textContent = isArabic() ? 'جارٍ تحميل الشروط…' : 'Loading policy…';
    body.append(status);
    try {
      const response = await fetch(link.href, { credentials: 'same-origin', signal: AbortSignal.timeout(20000) });
      if (!response.ok) throw new Error('Policy unavailable');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const guide = page.querySelector('[data-bt-policy-guide]');
      if (!guide) throw new Error('Policy content missing');
      // Reuse the actual policy and its original country/currency logic, not a second copy of the terms.
      guide.querySelectorAll('script').forEach((node) => node.remove());
      guide.removeAttribute('data-bt-policy-ready');
      await loadPolicyAsset(link.dataset.policyCss, true);
      body.replaceChildren(guide);
      await loadPolicyAsset(link.dataset.policyJs, false);
      guide.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
      policyLoaded = true;
      sync();
    } catch (_) {
      body.replaceChildren(status);
      status.textContent = isArabic() ? 'تعذّر تحميل الشروط. ' : 'Could not load the policy. ';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = isArabic() ? 'إعادة المحاولة' : 'Try again';
      retry.addEventListener('click', () => openPolicy(link));
      status.append(retry);
    } finally {
      policyLoading = false;
      body.removeAttribute('aria-busy');
    }
  }
  let acceptedContext = null;
  let appliedCurrency = '';
  const read = (key) => { try { return localStorage.getItem(key) || ''; } catch (_) { return ''; } };
  const normalize = (value) => String(value || '').toUpperCase().split('|')[0].replace(/[^A-Z]/g, '').slice(0, 3);
  function currency() {
    const panel = [...panels].find((item) => item.isConnected);
    const currency = panel?.dataset.currencyMode === '1'
      ? panel.dataset.currency
      : appliedCurrency || read('hdt_currency_iso') || read('T4Currency') || panel?.dataset.currency;
    return normalize(currency) || 'OMR';
  }
  function context() {
    const panel = [...panels].find((item) => item.isConnected);
    return [document.documentElement.lang, currency(), panel?.dataset.country || '', read('bt_policy_country_iso')].join('|');
  }
  function approved() { return currency() === 'OMR' || (acceptedContext !== null && acceptedContext === context()); }
  function sync() {
    const exempt = currency() === 'OMR';
    const allowed = approved();
    if (!allowed || exempt) acceptedContext = null;
    if (allowed) document.documentElement.setAttribute('data-bt-cart-policy-approved', 'true');
    else document.documentElement.removeAttribute('data-bt-cart-policy-approved');
    panels.forEach((panel) => {
      panel.hidden = exempt;
      const input = panel.querySelector('[data-bt-policy-checkbox]');
      if (input) {
        input.checked = !exempt && allowed;
        input.disabled = exempt;
      }
      const help = panel.querySelector('[data-bt-policy-help]');
      if (help) help.textContent = allowed ? help.dataset.approved : help.dataset.pending;
    });
    document.querySelectorAll('[data-bt-policy-express]').forEach((element) => {
      element.toggleAttribute('inert', !allowed);
    });
  }
  function block(event) {
    if (approved()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    sync();
    const panel = [...panels].find((item) => item.isConnected && item.getClientRects().length);
    panel?.querySelector('[data-bt-policy-checkbox]')?.focus();
  }
  class CartPolicy extends HTMLElement {
    connectedCallback() { panels.add(this); sync(); }
    disconnectedCallback() { panels.delete(this); }
  }
  customElements.define('bt-cart-policy', CartPolicy);
  function revealCheckout(input) {
    // Wait for checkout visibility and the cart layout to settle after consent.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (!input.isConnected || !input.checked || !approved()) return;
      const scope = input.closest('#CartDrawer, #hdt-page-cart');
      if (!scope) return;
      const button = [...scope.querySelectorAll('[data-bt-shopify-checkout-proxy], [data-bt-native-checkout]')]
        .find((element) => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
      if (!button) return;
      button.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }));
  }
  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-bt-policy-checkbox]')) return;
    acceptedContext = event.target.checked ? context() : null;
    sync();
    if (event.target.checked) revealCheckout(event.target);
  });
  document.addEventListener('click', (event) => {
    const policyLink = event.target.closest('[data-bt-policy-open]');
    if (policyLink) { event.preventDefault(); event.stopImmediatePropagation(); openPolicy(policyLink); return; }
    if (event.target.closest('[data-bt-native-checkout], [data-bt-shopify-checkout-proxy], [data-bt-policy-express]')) block(event);
  }, true);
  document.addEventListener('submit', (event) => {
    if (!['CartPage-Form', 'CartDrawer-Form'].includes(event.target.id)) return;
    // Quantity/update submissions remain available before accepting the policy.
    if (!event.submitter || event.submitter.name === 'checkout') block(event);
  }, true);
  ['currency:applied', 'currency:changed', 'currency:update'].forEach((name) => {
    document.addEventListener(name, (event) => {
      const detail = event.detail || {};
      appliedCurrency = normalize(detail.currency || detail.iso_code || detail.code || detail.to) || read('hdt_currency_iso') || read('T4Currency');
      sync();
      queueMicrotask(sync);
    });
  });
  ['cart:main:render', 'cart:drawer:render', 'shopify:section:load'].forEach((name) => {
    document.addEventListener(name, () => { sync(); queueMicrotask(sync); });
  });
  window.addEventListener('storage', (event) => {
    if (['hdt_currency_iso', 'T4Currency', 'bt_policy_country_iso'].includes(event.key)) {
      appliedCurrency = '';
      sync();
    }
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) acceptedContext = null;
    sync();
  });
})();
